/**
 * Contractor profile — Invoices tab.
 *
 * Every bill raised for the contractor (dswms_bill_lst_t.cntrctr_id), newest first, with
 * gross / deductions / net per bill and the totals above them. Reads
 * GET ds-wms/contractors/:id/invoices. Bills are created and moved through approval on
 * the Bills & Payments screen; this tab is the contractor-wise read of them.
 */
import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '@/services/interceptor';
import { TableSkeleton } from '@/components/base/Skeleton';
import { fmtMoney } from '../../lib/money';

interface Invoice {
  billId: number;
  billCode: string;
  raNumber: number | null;
  billType: string;
  billTypeCode: string;
  status: string;
  statusCode: string;
  grossAmount: number;
  deductions: number;
  netAmount: number;
  payableAmount: number;
  billDate: string | null;
  submittedDate: string | null;
  paidDate: string | null;
  workId: number | null;
  workCode: string;
  workName: string;
  contractCode: string;
}

interface InvoiceSummary {
  count: number;
  grossAmount: number;
  deductions: number;
  netAmount: number;
  paidCount: number;
  paidAmount: number;
  pendingAmount: number;
}

const STATUS_TONE: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  SUBMITTED: 'bg-blue-50 text-blue-700',
  CHECKED: 'bg-indigo-50 text-indigo-700',
  PASSED: 'bg-violet-50 text-violet-700',
  APPROVED: 'bg-amber-50 text-amber-700',
  PAID: 'bg-green-50 text-green-700',
  REJECTED: 'bg-red-50 text-red-700',
  CANCELLED: 'bg-red-50 text-red-700',
};

const fmtDate = (ymd: string | null) => (ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN') : '-');

interface Props {
  contractorId: number;
  onWorkIdClick: (workId: string) => void;
}

export default function ContractorInvoicesTab({ contractorId, onWorkIdClick }: Props) {
  const [rows, setRows] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`ds-wms/contractors/${contractorId}/invoices`);
      setRows(res.data?.data || []);
      setSummary(res.data?.summary || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-4"><TableSkeleton rows={4} columns={7} showToolbar={false} /></div>;

  if (error) {
    return (
      <div className="p-8 text-center text-sm text-red-600">
        <i className="ri-error-warning-line mr-1"></i>{error}
        <button onClick={load} className="ml-3 text-blue-600 hover:underline">Retry</button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
            <i className="ri-bill-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">No bills yet</h3>
          <p className="mt-1 text-xs text-gray-500">Bills raised for this contractor on Bills &amp; Payments will be listed here.</p>
        </div>
      </div>
    );
  }

  const cards = summary ? [
    { label: 'Bills', value: String(summary.count), sub: `${summary.paidCount} paid`, tone: 'text-slate-800' },
    { label: 'Gross Billed', value: fmtMoney(summary.grossAmount), sub: 'before deductions', tone: 'text-slate-800' },
    { label: 'Deductions', value: fmtMoney(summary.deductions), sub: 'taxes, recoveries', tone: 'text-red-600' },
    { label: 'Net Paid', value: fmtMoney(summary.paidAmount), sub: 'bills marked Paid', tone: 'text-green-700' },
    { label: 'Net Pending', value: fmtMoney(summary.pendingAmount), sub: 'not yet paid', tone: 'text-amber-700' },
  ] : [];

  return (
    <div className="space-y-3 p-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{c.label}</div>
            <div className={`text-base font-bold ${c.tone}`}>{c.value}</div>
            <div className="text-[10px] text-gray-400">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Bill No.</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Work</th>
              <th className="px-3 py-2 text-left">Bill Date</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className="px-3 py-2 text-right">Deductions</th>
              <th className="px-3 py-2 text-right">Net</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Paid On</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.billId} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{r.billCode}</td>
                <td className="px-3 py-2 text-gray-700">
                  {r.billType}{r.raNumber ? <span className="text-gray-400"> #{r.raNumber}</span> : null}
                </td>
                <td className="max-w-[220px] px-3 py-2">
                  {r.workId ? (
                    <button onClick={() => onWorkIdClick(String(r.workId))} className="block max-w-full truncate text-left text-blue-600 hover:underline" title={r.workName}>
                      {r.workCode || r.workName}
                    </button>
                  ) : <span className="text-gray-400">-</span>}
                  {r.workCode && r.workName && <div className="truncate text-[10px] text-gray-400" title={r.workName}>{r.workName}</div>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.billDate)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-gray-700">{fmtMoney(r.grossAmount)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-red-600">{r.deductions ? fmtMoney(r.deductions) : '-'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-semibold text-gray-900">{fmtMoney(r.netAmount)}</td>
                <td className="px-3 py-2">
                  <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[r.statusCode] || 'bg-gray-100 text-gray-600'}`}>{r.status || '-'}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDate(r.paidDate)}</td>
              </tr>
            ))}
          </tbody>
          {summary && (
            <tfoot className="bg-gray-50 font-semibold text-gray-800">
              <tr>
                <td className="px-3 py-2" colSpan={4}>Total ({summary.count})</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(summary.grossAmount)}</td>
                <td className="px-3 py-2 text-right font-mono text-red-600">{fmtMoney(summary.deductions)}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtMoney(summary.netAmount)}</td>
                <td className="px-3 py-2" colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
